<template>
  <section class="main-card">
    <div class="hero">
      <div class="headline">
        <p class="eyebrow">Software Engineer · Amazon alum</p>
        <h1>Tomas Prieto</h1>
        <p class="lead">2+ years crafting polished experiences at Amazon, shipping full-stack features and mobile apps that are reliable, scalable, and human-friendly.</p>
        <div class="actions">
          <a class="btn primary" href="https://docs.google.com/document/d/1_xzNGjhWN4SMaoioDcdMhclgFXi4WYHUhF28vb_Ippw/edit?usp=sharing" target="_blank" rel="noopener">View Resume</a>
          <a class="btn ghost" href="https://www.linkedin.com/in/tomasaprieto/" target="_blank" rel="noopener">LinkedIn</a>
          <a class="btn text" href="mailto:prietotomas01@gmail.com">Email me</a>
        </div>
        <div class="meta">
          <div class="chip">Full-stack & mobile</div>
          <div class="chip">Cloud-first delivery</div>
          <div class="chip">End-to-end ownership</div>
        </div>
      </div>
      <div class="profile-card">
        <img src="../assets/profile_pic.png" alt="Portrait of Tomas Prieto" class="avatar">
        <div class="profile-grid">
          <div>
            <p class="label">Role</p>
            <p class="value">Software Developer</p>
          </div>
          <div>
            <p class="label">Experience</p>
            <p class="value">2+ yrs @ Amazon</p>
          </div>
          <div>
            <p class="label">Phone</p>
            <p class="value"><a href="tel:+16616192495">(661) 619-2495</a></p>
          </div>
          <div>
            <p class="label">Email</p>
            <p class="value"><a class="email-link" href="mailto:prietotomas01@gmail.com">prietotomas01@gmail.com</a></p>
          </div>
        </div>
      </div>
    </div>

    <div class="content">
      <div class="card">
        <p class="eyebrow">About</p>
        <h2>Building thoughtful products with pace and polish</h2>
        <p>Software Engineer with 2+ years at Amazon and prior internships. I focus on scalable systems, automation, and full-stack apps—driving cost savings (>$600K/year) and stronger security through pragmatic tooling. I like solving messy problems and shipping outcomes that matter.</p>
      </div>

      <div class="card experience">
        <p class="eyebrow">Experience</p>
        <h2>Impact snapshots</h2>
        <div class="timeline">
          <div v-for="item in experiences" :key="item.company" class="timeline-item">
            <div class="timeline-header">
              <div>
                <p class="label">{{ item.role }}</p>
                <p class="value">{{ item.company }} · {{ item.location }}</p>
              </div>
              <span class="badge">{{ item.dates }}</span>
            </div>
            <ul>
              <li v-for="point in item.points" :key="point">{{ point }}</li>
            </ul>
          </div>
        </div>
      </div>

      <div class="card skills">
        <div class="skills-header">
          <div>
            <p class="eyebrow">Capabilities</p>
            <h2>Technical toolkit</h2>
          </div>
          <div class="badge">Always learning</div>
        </div>
        <div class="skills-grid">
          <div class="skill-group">
            <p class="label">Languages</p>
            <div class="pill-row">
              <span v-for="lang in languages" :key="lang" class="pill">{{ lang }}</span>
            </div>
          </div>
          <div class="skill-group">
            <p class="label">Frameworks</p>
            <div class="pill-row">
              <span v-for="framework in frameworks" :key="framework" class="pill">{{ framework }}</span>
            </div>
          </div>
          <div class="skill-group">
            <p class="label">Cloud & Data</p>
            <div class="pill-row">
              <span v-for="cloud in clouds" :key="cloud" class="pill">{{ cloud }}</span>
            </div>
          </div>
          <div class="skill-group">
            <p class="label">Tools</p>
            <div class="pill-row">
              <span v-for="tool in tools" :key="tool" class="pill">{{ tool }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="card education">
        <p class="eyebrow">Education</p>
        <h2>California Polytechnic State University, San Luis Obispo</h2>
        <p class="value">B.S. in Software Engineering</p>
        <p class="label">Sep 2019 – Jun 2023</p>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'MainCard',
  data() {
    return {
      languages: ['Python', 'TypeScript', 'JavaScript', 'Java', 'C++', 'C', 'Kotlin', 'SQL'],
      frameworks: ['React Native', 'React', 'Vue', 'Node', 'Angular', 'Flask', 'Bootstrap', 'jQuery'],
      clouds: ['AWS', 'GCP', 'Azure', 'DynamoDB', 'MongoDB', 'Firebase'],
      tools: ['Unity', 'Android Studio', 'Linux/Unix', 'Jira', 'Azure DevOps', 'Git'],
      experiences: [
        {
          role: 'Software Engineer',
          company: 'Amazon',
          location: 'Irvine, CA',
          dates: 'Feb 2024 – Feb 2026',
          points: [
            'Designed automated shutdown for unused EC2 game servers, saving $600K+ annually.',
            'Improved DevOps tooling for Amazon Games Studios’ New World with cross-functional teams.'
          ]
        },
        {
          role: 'Software Engineering Intern',
          company: 'Amazon',
          location: 'Irvine, CA',
          dates: 'Jun 2022 – Sep 2022',
          points: [
            'Built and deployed a full-stack anti-exploit tool (JavaScript, Python) to cut trade exploit detection time and boost player trust.'
          ]
        },
        {
          role: 'Software Engineering Intern',
          company: 'Chevron',
          location: 'Bakersfield, CA',
          dates: 'Jun 2020 – Aug 2020',
          points: ['Automated drone data logging via JavaScript + DroneDeploy APIs, streamlining field workflows.']
        },
        {
          role: 'Cybersecurity Research',
          company: 'Chevron',
          location: 'Bakersfield, CA',
          dates: 'Jun 2018 – Jul 2018',
          points: ['Used bash and C++ to crack 90% of challenge passwords during a security study.']
        }
      ]
    }
  }
}
</script>

<style scoped>
.main-card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 24px;
  padding: 28px;
  backdrop-filter: blur(14px);
  box-shadow: 0 20px 80px rgba(0, 0, 0, 0.25);
}

.hero {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

.headline h1 {
  margin: 4px 0 8px;
  font-size: 32px;
  letter-spacing: -0.5px;
}

.headline .lead {
  color: var(--muted);
  margin: 0 0 16px;
  line-height: 1.6;
}

.eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 12px;
  margin: 0;
  color: var(--muted);
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 12px;
  font-weight: 600;
  text-decoration: none;
  color: var(--text);
  border: 1px solid transparent;
  transition: transform 0.1s ease, box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
}

.btn.primary {
  background: linear-gradient(120deg, var(--accent), var(--accent-2));
  color: #051026;
  border-color: rgba(255, 255, 255, 0.08);
}

.btn.ghost {
  background: var(--card-strong);
  border-color: var(--border);
}

.btn.text {
  padding: 10px 8px;
  color: var(--muted);
  border-color: transparent;
}

.btn.text:hover {
  color: var(--text);
}

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  background: var(--card-strong);
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid var(--border);
  font-size: 13px;
  color: var(--text);
}

.profile-card {
  background: var(--card-strong);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 16px;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 14px;
  align-items: center;
}

.avatar {
  width: 96px;
  height: 96px;
  border-radius: 18px;
  object-fit: cover;
  border: 1px solid var(--border);
}

.profile-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
}

.label {
  margin: 0;
  font-size: 12px;
  color: var(--muted);
}

.value {
  margin: 2px 0 0;
  font-weight: 600;
}

.value a {
  color: inherit;
  text-decoration: none;
}

.value a:hover {
  color: var(--accent);
}

.email-link {
  font-size: 14px;
  display: inline-block;
}


.content {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  margin-top: 18px;
}

.card {
  background: var(--card-strong);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 18px;
}

.card h2 {
  margin: 6px 0 10px;
  font-size: 22px;
}

.card p {
  margin: 0;
  color: var(--muted);
  line-height: 1.6;
}

.skills {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.education {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.experience {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.timeline {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.timeline-item {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 12px;
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
}

.timeline-item ul {
  margin: 8px 0 0 16px;
  color: var(--muted);
  padding: 0;
}

.timeline-item li + li {
  margin-top: 4px;
}

.skills-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.badge {
  padding: 6px 12px;
  background: var(--card);
  border-radius: 999px;
  border: 1px solid var(--border);
  color: var(--muted);
  font-weight: 600;
  font-size: 13px;
}

.skills-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.skill-group {
  background: var(--card);
  border-radius: 12px;
  padding: 12px;
  border: 1px solid var(--border);
}

.pill-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.pill {
  padding: 6px 10px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid var(--border);
  font-size: 13px;
}

@media (min-width: 900px) {
  .hero {
    grid-template-columns: 2fr 1.1fr;
    gap: 24px;
    align-items: start;
  }

  .content {
    grid-template-columns: repeat(2, 1fr);
  }

  .experience {
    grid-row: span 3;
  }

  .skills {
    height: 100%;
  }

  .skills-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 520px) {
  .profile-card {
    grid-template-columns: 1fr;
    text-align: left;
  }

  .profile-grid {
    grid-template-columns: 1fr 1fr;
  }

  .actions {
    flex-direction: column;
    align-items: flex-start;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
